import React, { useEffect, useRef } from "react";
import { FormProvider } from "react-hook-form";
import { Button, Dialog, Form } from "@sscs/ui";
import { SoftwareInfo } from "Components";
import { FileDetails } from "../../types";
import useEditSoftwareInfoForm from "./useEditSoftwareInfoForm";

type EditSoftwareInfoProps = {
  file: FileDetails;
  onClose: () => void;
  isOpen: boolean;
};

const EditSoftwareInfoContent = ({
  file,
  onClose,
}: Omit<EditSoftwareInfoProps, "isOpen">): React.JSX.Element => {
  const versionInputRef = useRef<HTMLInputElement>(null);

  const { onSubmit, formMethods } = useEditSoftwareInfoForm({
    onClose,
    fileId: file.id,
    fileType: file.type,
    softwareInfo: file.software_info,
  });

  const version = formMethods.watch("version");
  const showWarning =
    file.type === "VERSION" && version !== file.software_info.version;

  useEffect(() => {
    if (file.type !== "VERSION") return;

    // fix browser autocomplete positioning
    versionInputRef.current?.blur();
    versionInputRef.current?.focus();
  }, [file.type]);

  return (
    <FormProvider {...formMethods}>
      <Form.Form onSubmit={onSubmit} name="software-info-form" horizontal dense>
        <Dialog.Header>
          <Dialog.Header.Title>Edit software info</Dialog.Header.Title>
        </Dialog.Header>
        <Dialog.Body>
          {file.type === "VERSION" ? (
            <SoftwareInfo.Form
              {...(file.imported_from
                ? { importUrl: file.imported_from }
                : { fileName: file.fileName })}
              versionInputRef={versionInputRef}
              isVersion
              offerAnalysisNotification={false}
              title={null}
            />
          ) : (
            <SoftwareInfo.Form
              {...(file.imported_from
                ? { importUrl: file.imported_from }
                : { fileName: file.fileName })}
              isVersion={false}
              offerAnalysisNotification={false}
              title={null}
            />
          )}
          {showWarning && (
            <Dialog.Banner
              type="warning"
              title="Renaming your version will also change its report URL"
            >
              If you shared the old link, it will no longer work.
            </Dialog.Banner>
          )}
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.Footer.Actions>
            <Button color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" color="primary">
              Save
            </Button>
          </Dialog.Footer.Actions>
        </Dialog.Footer>
      </Form.Form>
    </FormProvider>
  );
};

const EditSoftwareInfo = ({
  isOpen,
  ...props
}: EditSoftwareInfoProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <EditSoftwareInfoContent {...props} />
  </Dialog.Frame>
);

export default EditSoftwareInfo;
